<app-rc-modal
    [modalId]="modalId"
    [noIcon]="true"
    modalTitle="Déclaration du jour"
    [closable]="false"
    [noButton]="true"
    [width]="'820px'"
    modalTitleIcon="fa-solid fa-calendar-day"
    modalSubtitle="Renseignez votre activité quotidienne">

    <!-- Largeur fixe indépendante de viewMode : la modale ne doit jamais changer de taille au bascule jour/semaine.
         mx-auto est indispensable : sur les grands écrans (2xl:min-w-[30%] dans app-rc-modal), la carte peut être
         plus large que ces 820px fixes, et sans marge auto le contenu colle au bord gauche au lieu d'être centré. -->
    <div class="w-[820px] max-w-[92vw] p-6 mx-auto">
        <!-- Header with date -->
        <div class="mb-4 text-center">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-main_color/10 dark:bg-main_color/20 mb-3">
                <i class="fa fa-calendar-check text-main_color text-2xl"></i>
            </div>
            <h3 class="text-lg font-semibold text-slate-800 dark:text-slate-200 capitalize">
                {{ todayFormatted() }}
            </h3>
            <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                @if (viewMode() === 'week') {
                    Complétez les jours restants de votre semaine.
                } @else {
                    Veuillez renseigner votre activité pour aujourd'hui
                }
            </p>
        </div>

        <!-- Le seul point d'entree vers la vue semaine est le nudge "Il vous reste N jours"
             en bas une fois la journee remplie : pas de bouton "Remplir ma semaine" ici pour
             eviter le doublon. En vue semaine, ce bouton reste le seul moyen de revenir. -->
        @if (viewMode() === 'week') {
        <div class="mb-4 flex justify-center">
            <button type="button" (click)="viewMode.set('day')"
                class="inline-flex items-center gap-2 rounded-lg border border-main_color/30 bg-main_color/5 px-3 py-2 text-xs font-semibold text-main_color transition-colors hover:bg-main_color/10 focus-visible:ring-2 focus-visible:ring-main_color dark:border-main_color/40 dark:bg-main_color/10 dark:text-main_color_2 dark:hover:bg-main_color/15">
                <i class="fa-solid fa-calendar-day"></i>
                Retour à ma journée
            </button>
        </div>
        }

        @if (isLoadingWeek()) {
            <div class="mx-auto max-w-2xl space-y-6" aria-busy="true" aria-live="polite">
                <span class="sr-only">Chargement de votre déclaration</span>
                <div class="grid grid-cols-2 gap-4">
                    @for (period of ['morning', 'afternoon']; track period) {
                        <div class="animate-pulse">
                            <div class="mb-1.5 h-3.5 w-16 rounded bg-slate-200/80 dark:bg-slate-700/80"></div>
                            <div class="space-y-1">
                                <div class="grid grid-cols-2 gap-1">
                                    <div class="h-8 rounded-md border border-slate-200/80 bg-slate-100/80 dark:border-slate-700/80 dark:bg-slate-800/80"></div>
                                    <div class="h-8 rounded-md border border-slate-200/80 bg-slate-100/80 dark:border-slate-700/80 dark:bg-slate-800/80"></div>
                                </div>
                                <div class="h-9 rounded-md border border-slate-200/80 bg-slate-100/80 dark:border-slate-700/80 dark:bg-slate-800/80"></div>
                            </div>
                        </div>
                    }
                </div>
                <div class="animate-pulse">
                    <div class="mb-2 h-3.5 w-24 rounded bg-slate-200/80 dark:bg-slate-700/80"></div>
                    <div class="h-20 rounded-lg border border-slate-200/80 bg-slate-100/80 dark:border-slate-700/80 dark:bg-slate-800/80"></div>
                </div>
            </div>
        } @else if (viewMode() === 'day') {

        <!-- Form -->
        <div class="mx-auto max-w-2xl space-y-6">
            <!-- Matin/Après-midi côte à côte : à cette largeur (max-w-2xl), les empiler comme sur
                 un mobile laissait un vide énorme de chaque côté sans raison. -->
            <div class="grid grid-cols-2 gap-4">
                <!-- Morning activity -->
                <!-- div plutôt que fieldset : une règle globale (styles.css) force le placeholder
                     des ng-select en text-slate-50 dès qu'ils sont dans un <fieldset>, pensée pour
                     un fond sombre ailleurs dans l'appli. Ici le select garde son fond blanc par
                     défaut -> texte blanc sur blanc, le libellé "Autres" devient invisible. -->
                <div class="min-w-0">
                    <label class="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                        <i class="fa fa-sun text-amber-500 dark:text-amber-400"></i> Matin
                    </label>
                    <app-activity-picker [value]="currentDay()?.morning ?? ''" (valueChange)="setActivity('morning', $event)">
                    </app-activity-picker>
                </div>

                <!-- Afternoon activity -->
                <div class="min-w-0">
                    <label class="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                        <i class="fa fa-moon text-indigo-500 dark:text-indigo-400"></i> Après-midi
                    </label>
                    <app-activity-picker [value]="currentDay()?.afternoon ?? ''" (valueChange)="setActivity('afternoon', $event)">
                    </app-activity-picker>
                </div>
            </div>

            <!-- Ticket Restaurant -->
            @if (shouldShowTicketRestaurant()) {
                <div class="flex items-center justify-between p-4 m-0 rounded-xl bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 mt-4">
                    <div class="flex items-center gap-3">
                        <div class="flex items-center justify-center w-10 h-10 rounded-full bg-main_color_2/10 dark:bg-main_color_2/20">
                            <i class="fa fa-utensils text-main_color_2"></i>
                        </div>
                        <div>
                            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">Ticket Restaurant</span>
                            <p class="text-xs text-slate-500 dark:text-slate-400">Cochez si vous bénéficiez d'un ticket restaurant aujourd'hui</p>
                        </div>
                    </div>
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            [checked]="currentDay()?.ticketRestaurant"
                            (change)="onTicketRestaurantChange($any($event.target).checked)"
                            class="sr-only peer">
                        <div class="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-main_color/30 dark:peer-focus:ring-main_color/50 rounded-full peer dark:bg-slate-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-main_color_2"></div>
                    </label>
                </div>
            }

            <app-input-rc class="mt-4"
                [form]="commentForm"
                formControlNameRc="comment"
                label="Commentaire"
                icon="fa fa-comment"
                tag="textarea"
                [rows]="3"
                placeholder="Ajoutez un commentaire si nécessaire..."
                [noMargin]="true">
            </app-input-rc>
            @if (shouldShowTicketRestaurant() && !currentDay()?.ticketRestaurant) {
                <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Vous pouvez préciser ici pourquoi le ticket restaurant est retiré.
                </p>
            }
        </div>

        <div class="mx-auto max-w-2xl">
            <!-- Validation message -->
            @if (!(currentDay()?.morning && currentDay()?.afternoon)) {
                <div class="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <div class="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                        <i class="fa fa-exclamation-triangle text-sm"></i>
                        <span class="text-sm">Veuillez renseigner les deux périodes pour valider</span>
                    </div>
                </div>
            }

            <!-- Nudge : proposer de compléter la semaine une fois le jour rempli -->
            @if (showWeekNudge()) {
                <div class="mt-4 flex items-center justify-between gap-3 rounded-lg border border-main_color_2/30 bg-main_color_2/5 p-3 dark:border-main_color_2/40 dark:bg-main_color_2/10">
                    <p class="text-xs text-slate-600 dark:text-slate-300">
                        <i class="fa-solid fa-circle-info mr-1 text-main_color_2"></i>
                        Il vous reste {{ remainingIncompleteDays() }} jour{{ remainingIncompleteDays() > 1 ? 's' : '' }} à compléter cette semaine.
                    </p>
                    <button type="button" (click)="viewMode.set('week')"
                        class="shrink-0 rounded-lg border border-main_color_2/40 bg-white px-2.5 py-1.5 text-xs font-semibold text-main_color_2 transition-colors hover:bg-main_color_2/10 dark:bg-slate-900">
                        Remplir ma semaine
                    </button>
                </div>
            }
        </div>

        } @else {

        <!-- Vue semaine -->
        <p class="mb-3 text-center text-xs text-slate-500 dark:text-slate-400">
            {{ completedDaysCount() }}/{{ weekDays().length }} jours renseignés
        </p>

        <div class="flex items-center gap-2">
            <button type="button" (click)="scrollWeek(-1)"
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-sm text-slate-600 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
                <i class="fas fa-chevron-left"></i>
            </button>

            <div #weekScroll class="grid min-w-0 flex-1 grid-flow-col auto-cols-[minmax(220px,240px)] gap-3 overflow-x-auto scroll-smooth pb-2">
                @for (day of weekDays(); track day.date) {
                <section [class]="'rounded-xl border p-3 transition-colors ' +
                    (day.date === currentDay()?.date
                        ? 'border-main_color/40 bg-main_color/5 dark:border-main_color/50 dark:bg-main_color/10'
                        : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/60')"
                    [attr.aria-label]="'Déclaration du ' + day.label">
                    <div class="mb-3 flex items-center gap-1.5">
                        <h4 class="text-sm font-semibold capitalize text-slate-800 dark:text-slate-100">{{ day.label }}</h4>
                        @if (day.date === currentDay()?.date) {
                            <span class="rounded-full bg-main_color px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">Auj.</span>
                        }
                    </div>

                    <div class="space-y-3">
                        <div>
                            <p class="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Matin</p>
                            <app-activity-picker [value]="day.morning"
                                (valueChange)="setActivityForDate(day.date, 'morning', $event)">
                            </app-activity-picker>
                        </div>

                        <div>
                            <p class="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Après-midi</p>
                            <app-activity-picker [value]="day.afternoon"
                                (valueChange)="setActivityForDate(day.date, 'afternoon', $event)">
                            </app-activity-picker>
                        </div>
                    </div>

                    @if (shouldShowTicketRestaurantFor(day)) {
                        <label class="mt-3 flex cursor-pointer items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                            <input type="checkbox" [checked]="day.ticketRestaurant"
                                (change)="onTicketRestaurantInput(day.date, $event)"
                                class="h-4 w-4 rounded border-slate-300 text-main_color focus:ring-main_color dark:border-slate-600 dark:bg-slate-800">
                            Ticket restaurant
                        </label>
                    }

                    <input type="text" [value]="day.comment" (input)="onCommentInputForDate(day.date, $event)"
                        class="mt-3 w-full rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 text-xs text-slate-800 placeholder:text-slate-400 focus:border-main_color focus:ring-1 focus:ring-main_color dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
                        [attr.aria-label]="'Commentaire du ' + day.label" placeholder="Commentaire (facultatif)">
                </section>
                }
            </div>

            <button type="button" (click)="scrollWeek(1)"
                class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-sm text-slate-600 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300">
                <i class="fas fa-chevron-right"></i>
            </button>
        </div>

        }

        <!-- Submit button -->
        <div [class]="'mt-8 ' + (viewMode() === 'day' ? 'mx-auto max-w-2xl' : '')">
            <button
                (click)="submit()"
                [disabled]="!canSubmit()"
                class="w-full px-6 py-3 rounded-xl font-medium text-white transition-all
                       disabled:bg-slate-300 disabled:dark:bg-slate-600 disabled:cursor-not-allowed
                       bg-main_color hover:bg-main_color/90
                       focus:ring-4 focus:ring-main_color/30 dark:focus:ring-main_color/50">
                @if (isSaving()) {
                    <i class="fa fa-spinner fa-spin mr-2"></i>
                    Enregistrement...
                } @else {
                    <i class="fa fa-check mr-2"></i>
                    Valider ma déclaration
                }
            </button>
        </div>

        <!-- Info text -->
        <p class="mt-4 text-xs text-center text-slate-400 dark:text-slate-500">
            Cette déclaration est obligatoire. Vous pouvez modifier vos déclarations mensuelles
            dans la section "Mes Déclarations" du module SIRH.
        </p>
    </div>

</app-rc-modal>
